import React, { useState, useEffect } from "react";
import Layout from "../../Components/Layout";
import Styles from "./index.module.css";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { Services } from "../../Services";
import ReactLoading from "react-loading";
import { warningSwal } from "../../Util/Toast";
import { Link } from "react-router-dom";
import Table from "./Table";
import Select from "react-select";
import moment from "moment";

function TeacherManualAdjustmentReport() {
    const { register, handleSubmit, setValue, watch, formState: { errors }, reset } = useForm();
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [link, setLink] = useState("");
    const [tutorOptions, setTutorOptions] = useState([]);
    const [tutorSearch, setTutorSearch] = useState("");
    const [selectedTutor, setSelectedTutor] = useState(null);
    const [tutorSearchLoad, setTutorSearchLoad] = useState(false);
    
    const token = { Authorization: `token ${localStorage.getItem("token")}` };

    useEffect(() => {
        const debouncedSearch = setTimeout(async () => {
            if (typeof tutorSearch === "string" && tutorSearch.length > 0) {
                setTutorSearchLoad(true);
                try {
                    const data = await Services.searchActiveTutor(
                        "POST",
                        JSON.stringify({ SearchString: tutorSearch }),
                        token
                    );
                    const formattedOptions = data.Tutors?.filter(
                        (item) => item.TutorStatus == 1
                    )?.map((tutor) => ({
                        label: `${tutor.FirstName} ${tutor.LastName}`,
                        value: tutor.TutorID,
                    }));
                    setTutorOptions(formattedOptions || []);
                } catch (error) {
                    console.error("Error fetching tutors:", error);
                    setTutorOptions([]);
                } finally {
                    setTutorSearchLoad(false);
                }
            } else {
                setTutorOptions([]);
            }
        }, 350);

        return () => clearTimeout(debouncedSearch);
    }, [tutorSearch]);

    const onSubmit = async (data) => {
        if (!data.FromDate || !data.ToDate) {
            warningSwal("Warning", "From Date and To Date are required");
            return;
        }
        if (data.FromDate > data.ToDate) {
            warningSwal("Warning", "Please select a valid date range");
            return;
        }

        const start = moment(data.FromDate);
        const end = moment(data.ToDate);
        if (end.diff(start, 'days') > 30) {
            warningSwal("Warning", "Date range cannot exceed 30 days");
            return;
        }
        
        const TutorID = selectedTutor?.value || null;
        setLoading(true);
        try {
            const res = await Services.teacher_manual_adjustment_report(
                "GET",
                null,
                token,
                data.FromDate,
                data.ToDate,
                data.Type || "all",
                TutorID
            );
            setLoading(false);
            if (res?.Status === 1) {
                setLink(res.CsvFile || "");
                setResults(
                    res.Results?.map((item) => ({
                        "Teacher": item.TeacherName,
                        "Class Date": item.ClassDateTime && item.ClassDateTime !== "0000-00-00 00:00:00" ? moment.utc(item.ClassDateTime).local().format("DD-MM-YYYY hh:mm A") : "NA",
                        "Batch Name": <Link to={`/Batches/Details?BatchId=${item.BatchID}`} target="_blank" rel="noopener noreferrer" style={{ color: "#1e87db", textDecoration: "underline" }}>{item.BatchName}</Link>,
                        "Type": item.TransactionType,
                        "Amount": item.Amount,
                        "Reason": item.Reason,
                        "Comments": item.Comments,
                        "Transaction By": item.TransactionBy,
                        "Transaction Date": item.TransactionDate && item.TransactionDate !== "0000-00-00" ? moment(item.TransactionDate).format("DD-MM-YYYY") : "NA",
                    })) || []
                );
            } else {
                console.error(res);
                warningSwal("Warning", res?.message || "Failed to fetch report");
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
            warningSwal("Warning", "Something went wrong");
        }
    };

    return (
        <Layout Active={"Teacher Manual Adj. Report"}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Teacher Manual Adjustment Report</title>
            </Helmet>
            <div className={Styles["Container"]}>
                <div className={Styles["wrapper"]}>
                    <h3>Teacher Manual Adjustment Report</h3>
                    <form onSubmit={handleSubmit(onSubmit)} style={{ alignItems: "flex-start" }}>
                        <div className={Styles["form-control"]} style={{ flex: 1.5, minWidth: '200px' }}>
                            <label>Teacher</label>
                            <div style={{ width: '100%' }}>
                                <Select
                                    options={tutorOptions}
                                    onInputChange={(val) => setTutorSearch(val)}
                                    onChange={(val) => setSelectedTutor(val)}
                                    value={selectedTutor}
                                    isClearable
                                    placeholder="Search teacher by name"
                                    isLoading={tutorSearchLoad}
                                />
                            </div>
                        </div>

                        <div className={Styles["form-control"]} style={{ flex: 1, minWidth: '130px' }}>
                            <label>From Date <span className="important">*</span></label>
                            <input type="date" {...register("FromDate", { required: true })} />
                            {errors.FromDate && <span style={{ color: 'red', fontSize: '12px' }}>From Date is required</span>}
                        </div>
                        <div className={Styles["form-control"]} style={{ flex: 1, minWidth: '130px' }}>
                            <label>To Date <span className="important">*</span></label>
                            <input type="date" {...register("ToDate", { required: true })} />
                            {errors.ToDate && <span style={{ color: 'red', fontSize: '12px' }}>To Date is required</span>}
                        </div>
                        <div className={Styles["form-control"]} style={{ flex: 0.8, minWidth: '100px' }}>
                            <label>Type</label>
                            <select {...register("Type")} defaultValue={"all"}>
                                <option value="all">All</option>
                                <option value="Credit">Credit</option>
                                <option value="Debit">Debit</option>
                            </select>
                        </div>

                        <div className={Styles["form-control"]}>
                            <label style={{ visibility: "hidden" }}>Action</label>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                {(() => {
                                    const currentFilter = (watch("Type") || "all").toString().toLowerCase();
                                    const submitClass = `${Styles.btn} ${
                                        currentFilter === "credit" ? Styles.btnSuccess : currentFilter === "debit" ? Styles.btnDanger : Styles.btnPrimary
                                    }`;
                                    const exportClass = `${Styles.btn} ${
                                        currentFilter === "credit" ? Styles.btnSuccess : currentFilter === "debit" ? Styles.btnDanger : Styles.btnExport
                                    }`;

                                    return (
                                        <>
                                            <button
                                                type="submit"
                                                disabled={loading}
                                                className={submitClass}
                                                aria-busy={loading}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                                    {loading && <ReactLoading type="spokes" color="#fff" height={14} width={14} />}
                                                    {loading ? "Submitting..." : "Submit"}
                                                </span>
                                            </button>

                                            <button
                                                type="button"
                                                className={Styles.btn}
                                                style={{
                                                    backgroundColor: "#f3f4f6",
                                                    color: "#374151",
                                                    border: "1px solid #d1d5db"
                                                }}
                                                onClick={() => {
                                                    reset();
                                                    setSelectedTutor(null);
                                                    setTutorSearch("");
                                                    setResults([]);
                                                    setLink("");
                                                }}
                                                disabled={loading}>
                                                Clear
                                            </button>

                                            {link && (
                                                <button
                                                    type="button"
                                                    className={exportClass}
                                                    onClick={() => window.open(link, "_blank")}>
                                                    Export
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </form>

                    {results && results.length > 0 && (
                        <div style={{ overflow: "auto", marginTop: 16 }}>
                            <Table data={results} style={{ minWidth: "1500px" }} />
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export default TeacherManualAdjustmentReport;
